import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { suppressGlobalErrorToastMeta } from "@/lib/query";

type InvalidationSpec = {
  queryKey: QueryKey;
  refetchType?: "active" | "inactive" | "all" | "none";
};

/**
 * Shared shape for the write-via-API mutations:
 * POST to the backend, then invalidate whichever cached queries the payload/response
 * touched. Invalidation runs on settle rather than only on success — a request can
 * fail on the client (timeout, dropped response) after the backend already applied
 * it, so stale caches still need to refresh in that case.
 */
export function useApiTransactionMutation<TPayload, TResponse>(
  mutationFn: (payload: TPayload) => Promise<TResponse>,
  getInvalidations: (payload: TPayload, data: TResponse | undefined) => Array<InvalidationSpec | false | null | undefined>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: suppressGlobalErrorToastMeta,
    mutationFn,
    onSettled: (data, _error, payload) => {
      for (const spec of getInvalidations(payload, data)) {
        if (!spec) continue;
        void queryClient.invalidateQueries({
          queryKey: spec.queryKey,
          ...(spec.refetchType ? { refetchType: spec.refetchType } : {}),
        });
      }
    },
  });
}
